/**
 * 時間前入札の受付開始 (= 受渡日前日 12:00 JST) 判定。
 *
 * 受渡日 D のスポット約定が確定する (D-1 10:00 締切 → 公表 → 取込) までは JEPX
 * 渡し契約分を含む需給ギャップ (= need) が確定せず、 時間前のポジションを根拠に
 * 発注できない。 そのため D の時間前入札は D-1 12:00 (JST) 以降のみ作成・提出
 * できる (spec `docs/specs/bidding/intraday-bidding.md` AC-BIN-089c)。
 *
 * frontend 判定は導線ガード (コマリボン・札作成ボタン・カウントダウン) 用で、
 * 実際の拒否は backend の受付開始ゲートが行う (= RPC 直叩き経路もそこで塞ぐ)。
 *
 * 受付開始時刻も GC・開場と同じく **JST の壁時計**で定義される業務概念なので、
 * 受渡日の暦日を JST として絶対時刻に落として `now` と比べる (#4765。 ローカル
 * 時刻で組むと非 JST 端末で受付開始が最大 9 時間ズレ、 開場判定 (marketOpen.ts)
 * とも食い違う)。
 */
import {
  addDays,
  formatDateDisplay,
  jstWallClockToInstant,
} from '~/models/dateTime'
import {
  INTRADAY_ACCEPTANCE_OPEN_HOUR,
  INTRADAY_ACCEPTANCE_OPEN_MINUTE,
} from '~/pages/IntradayBidding/constants/market'

/**
 * 受渡日前日 12:00 (JST) の絶対時刻。
 *
 * @param deliveryDate - 受渡日 (= カレンダー日 `Date`、 `models/dateTime` 冒頭の定義)
 */
export const acceptanceStartAt = (deliveryDate: Date): Date =>
  jstWallClockToInstant(
    addDays(deliveryDate, -1),
    INTRADAY_ACCEPTANCE_OPEN_HOUR,
    INTRADAY_ACCEPTANCE_OPEN_MINUTE,
  )

/**
 * 選択中の受渡日がまだ受付前かどうかを判定する。
 *
 * 12:00:00 ちょうどは受付開始済 (= 境界は受付側に含める。 backend の
 * `IntradayOrder.IsBeforeAcceptance` と同じ境界)。
 *
 * @param now - 現在時刻 (= 絶対時刻。 `~/models/clock` の `now()` 由来)
 * @param deliveryDate - 受渡日 (= カレンダー日 `Date`)
 * @returns true = 受付前 (= 札作成・提出の導線を塞ぐ)
 */
export const isBeforeAcceptance = (now: Date, deliveryDate: Date): boolean =>
  now.getTime() < acceptanceStartAt(deliveryDate).getTime()

/**
 * 受付開始時刻文字列を返す (= 受付前メッセージ表示用)。
 *
 * 表示も JST 壁時計 (= 定数そのまま)。 絶対時刻を経由してローカル整形すると
 * 非 JST 端末で判定 ([[isBeforeAcceptance]]) と食い違う文字列が出る。
 *
 * @param deliveryDate - 受渡日
 * @param language - 画面の表示言語
 * @returns 日本語は "YYYY/MM/DD HH:MM"、 英語は "YYYY-MM-DD HH:MM"
 */
export const acceptanceStartTimeLabel = (
  deliveryDate: Date,
  language = 'ja',
): string => {
  const h = String(INTRADAY_ACCEPTANCE_OPEN_HOUR).padStart(2, '0')
  const m = String(INTRADAY_ACCEPTANCE_OPEN_MINUTE).padStart(2, '0')
  return `${formatDateDisplay(addDays(deliveryDate, -1), language)} ${h}:${m}`
}
